import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Separator } from './ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  CheckCircle,
  FileText,
  User,
  Building2,
  Package,
  AlertCircle,
  ArrowRight,
  ChevronLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { POLICY_CONFIG, DynamicField } from './PolicyFormModal';
import { useBusinessLine, BUSINESS_LINES } from '../contexts/BusinessLineContext';

const BUSINESS_LINE_LABELS = Object.fromEntries(BUSINESS_LINES.map(l => [l.id, l.name]));

function formatBusinessLine(bl) {
  if (!bl) return '—';
  return BUSINESS_LINE_LABELS[bl.toLowerCase()] || bl;
}

function getProductConfig(businessLine, productName) {
  const lineConfig = POLICY_CONFIG[businessLine];
  if (!lineConfig) return null;
  return lineConfig.products.find((p) => p.name === productName) || null;
}

export function PolicyConversionModal({ open, onOpenChange, lead, onSuccess, apiUrl, isAdmin }) {
  const { availableLines } = useBusinessLine();
  const [step, setStep] = useState(2);
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [selectedBusinesses, setSelectedBusinesses] = useState([]);
  // Step 0: adding business types when lead has none
  const [newBtInput, setNewBtInput] = useState('');
  const [form, setForm] = useState({
    email: '',
    phone: '',
    birth_date: '',
    aseguradora: '',
    policy_number: '',
    product_type: '',
    start_date: '',
    end_date: '',
    premium: '',
    contributions: '',
    chargeback: '',
    payment_done: true,
  });
  // Dynamic product fields state
  const [detalles, setDetalles] = useState({});
  const [loading, setLoading] = useState(false);
  // Existing policy when lead was already pendiente_de_pago
  const [existingPolicy, setExistingPolicy] = useState(null);

  useEffect(() => {
    if (open && lead) {
      const bts = lead.business_types?.length
        ? lead.business_types
        : lead.business_plan
        ? [lead.business_plan]
        : [];
      setAllBusinesses(bts);
      setSelectedBusinesses([...bts]);
      setNewBtInput('');
      // step 0: no businesses → user must add them
      // step 1: multiple businesses → user chooses which to convert
      // step 2: single business or came from step 0/1 → policy form
      setStep(bts.length === 0 ? 0 : bts.length > 1 ? 1 : 2);
      setExistingPolicy(null);

      const isPending = lead.status === 'pendiente_de_pago';

      // Base form from lead data
      const baseForm = {
        email:         lead.email         || '',
        phone:         lead.phone         || '',
        birth_date:    lead.birth_date    || '',
        aseguradora:   lead.business_lines?.[0] || lead.business_line || '',
        policy_number: '',
        product_type:  '',
        start_date:    new Date().toISOString().split('T')[0],
        end_date:      '',
        premium:       lead.contributions || '',
        contributions: lead.contributions || '',
        chargeback:    '',
        payment_done:  true,
      };

      if (isPending && lead.converted_client_id) {
        // Fetch existing policy to pre-fill the form
        axios.get(`${apiUrl}/policies`, { params: { lead_id: lead.id } })
          .then(res => {
            const policies = res.data || [];
            const pending = policies.find(p => p.status === 'pending_payment') || policies[0];
            if (pending) {
              setExistingPolicy(pending);
              setForm({
                ...baseForm,
                aseguradora:   pending.business_line || baseForm.aseguradora,
                policy_number: pending.policy_number || '',
                product_type:  pending.product_type  || '',
                start_date:    pending.start_date     ? pending.start_date.split('T')[0] : baseForm.start_date,
                end_date:      pending.end_date       ? pending.end_date.split('T')[0]   : '',
                premium:       pending.premium        != null ? String(pending.premium)        : baseForm.premium,
                contributions: pending.contributions  != null ? String(pending.contributions)  : baseForm.contributions,
                chargeback:    pending.chargeback     != null ? String(pending.chargeback)     : '',
              });
              setDetalles(pending.detalles_producto || {});
            } else {
              setForm(baseForm);
              setDetalles({});
            }
          })
          .catch(() => {
            setForm(baseForm);
            setDetalles({});
          });
      } else {
        setForm(baseForm);
        setDetalles({});
      }
    }
  }, [open, lead?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!lead) return null;

  const allSelected = selectedBusinesses.length === allBusinesses.length;
  const remaining = allBusinesses.filter(b => !selectedBusinesses.includes(b));
  const allClosed = remaining.length === 0;

  // Derive dynamic product fields from current selection
  const productConfig = getProductConfig(form.aseguradora, form.product_type);
  const productFields = productConfig?.fields ?? [];
  const regularFields = productFields.filter(f => f.type !== 'checkbox');
  const checkboxFields = productFields.filter(f => f.type === 'checkbox');

  // Products grouped by category for the selector
  const lineConfig = POLICY_CONFIG[form.aseguradora];
  const productsByGroup = lineConfig
    ? lineConfig.products.reduce((acc, p) => {
        if (!acc[p.group]) acc[p.group] = [];
        acc[p.group].push(p.name);
        return acc;
      }, {})
    : {};

  // ── Step 0 handlers ──
  const addBtFromInput = () => {
    const trimmed = newBtInput.trim();
    if (!trimmed) return;
    if (!allBusinesses.includes(trimmed)) {
      const updated = [...allBusinesses, trimmed];
      setAllBusinesses(updated);
      setSelectedBusinesses(updated);
    }
    setNewBtInput('');
  };

  const removeBtFromStep0 = (bt) => {
    const updated = allBusinesses.filter(x => x !== bt);
    setAllBusinesses(updated);
    setSelectedBusinesses(updated);
  };

  const handleStep0Continue = () => {
    // All added types are selected; jump straight to policy form
    setSelectedBusinesses([...allBusinesses]);
    setStep(2);
  };

  // ── Step 1 handlers ──
  const toggleBusiness = (bt) =>
    setSelectedBusinesses(prev =>
      prev.includes(bt) ? prev.filter(x => x !== bt) : [...prev, bt]
    );

  const toggleAll = () =>
    setSelectedBusinesses(allSelected ? [] : [...allBusinesses]);

  const handleChange = (field, value) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleAseguradoraChange = (v) => {
    setForm(prev => ({ ...prev, aseguradora: v, product_type: '' }));
    setDetalles({});
  };

  const handleProductChange = (v) => {
    setForm(prev => ({ ...prev, product_type: v }));
    setDetalles({});
  };

  const setDetallesField = (key, val) =>
    setDetalles(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    if (!form.email.trim()) { toast.error('El email del cliente es obligatorio'); return; }
    if (!form.phone.trim()) { toast.error('El teléfono del cliente es obligatorio'); return; }
    if (!form.birth_date)   { toast.error('La fecha de nacimiento del cliente es obligatoria'); return; }
    if (!form.aseguradora)  { toast.error('La aseguradora es obligatoria'); return; }
    if (!form.policy_number.trim()) { toast.error('El número de póliza es obligatorio'); return; }
    if (!form.start_date || !form.end_date) { toast.error('La vigencia (fecha inicio y fin) es obligatoria'); return; }
    if (selectedBusinesses.length === 0) { toast.error('Selecciona al menos un negocio'); return; }

    const clientDataPatch = {
      email:          form.email.trim(),
      phone:          form.phone.trim(),
      birth_date:     form.birth_date,
      business_line:  form.aseguradora,
      business_lines: [form.aseguradora],
    };

    const wasAlreadyPending = lead.status === 'pendiente_de_pago';

    setLoading(true);
    try {
      let clientId = null;
      let updatedLead = null;

      // If the lead had no business types (step 0 path), persist the ones the user added
      const hadNoBusiness = !lead.business_types?.length && !lead.business_plan;
      const businessTypesPatch = hadNoBusiness
        ? { business_types: allBusinesses, business_lines: [form.aseguradora] }
        : {};

      if (form.payment_done) {
        const leadRes = await axios.put(`${apiUrl}/leads/${lead.id}`, {
          ...clientDataPatch,
          ...businessTypesPatch,
          status: 'ganado',
          converted_business_types: selectedBusinesses,
          remaining_business_types: remaining,
        });
        updatedLead = leadRes.data;
        clientId = leadRes.data?.converted_client_id || null;
      } else {
        // Backend creates client immediately with pendiente_de_pago status
        const leadRes = await axios.put(`${apiUrl}/leads/${lead.id}`, {
          ...clientDataPatch,
          ...businessTypesPatch,
          status: 'pendiente_de_pago',
        });
        updatedLead = leadRes.data;
        clientId = leadRes.data?.converted_client_id || null;
      }

      const policyStatus = form.payment_done ? 'active' : 'pending_payment';
      const policyLeadId = form.payment_done && allClosed ? null : lead.id;

      // If lead was already pendiente_de_pago, the backend PUT to /leads already activated
      // the existing client and policy — just skip all policy creation/update here.
      if (!wasAlreadyPending) {
        await axios.post(`${apiUrl}/policies`, {
          policy_number:    form.policy_number.trim(),
          client_id:        clientId,
          client_name:      lead.name,
          lead_id:          policyLeadId,
          agent_id:         lead.assigned_agent_id || null,
          agent_name:       lead.assigned_agent_name || null,
          business_line:    form.aseguradora,
          product_type:     form.product_type || selectedBusinesses.join(', '),
          premium:          parseFloat(form.premium) || 0,
          contributions:    parseFloat(form.contributions) || 0,
          start_date:       form.start_date,
          end_date:         form.end_date,
          chargeback:       parseFloat(form.chargeback) || 0,
          status:           policyStatus,
          detalles_producto: detalles,
          notes: `Generada automáticamente al convertir lead el ${new Date().toLocaleDateString('es-MX')}.`,
        });
      }

      if (form.payment_done && allClosed && wasAlreadyPending) {
        toast.success('¡Pago confirmado! Cliente activado y póliza marcada como Activa.');
      } else if (form.payment_done && allClosed) {
        toast.success('¡Lead convertido a cliente! Póliza creada como Activa.');
      } else if (form.payment_done && !allClosed) {
        toast.success(`Conversión parcial — póliza creada para: ${selectedBusinesses.join(', ')}.`, {
          description: `Quedan pendientes: ${remaining.join(', ')}. El lead permanece En Negociación.`,
        });
      } else {
        toast.success('Cliente creado como Pendiente de pago.', {
          description: 'El cliente ya aparece en la pestaña Clientes. Se enviará notificación diaria al agente.',
        });
      }

      onOpenChange(false);
      // removeFromList: full conversion paid OR pending payment → remove from leads list
      // Partial conversion paid → keep in list (lead stays en_negociacion)
      const removeFromList = !form.payment_done || allClosed;
      if (onSuccess) onSuccess({ converted: form.payment_done && allClosed, removeFromList, lead: updatedLead });
    } catch (err) {
      console.error('Error al procesar lead:', err);
      toast.error('Error al procesar', {
        description: err.response?.data?.detail || 'Intenta de nuevo',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1a365d]">
            <CheckCircle className="h-5 w-5 text-green-500" />
            Convertir Lead a Cliente
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            {step === 0
              ? `Agrega el tipo de negocio o producto para ${lead.name}`
              : step === 1
              ? `¿Qué negocios se cerraron para ${lead.name}?`
              : `Datos de la póliza para ${lead.name}`}
          </DialogDescription>
        </DialogHeader>

        {/* ── Step 0: Add business types (lead had none) ── */}
        {step === 0 && (
          <>
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                Este lead no tiene ningún tipo de negocio o producto asignado.
                Agrega al menos uno antes de continuar.
              </span>
            </div>

            <div className="space-y-3 py-1">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newBtInput}
                  onChange={e => setNewBtInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addBtFromInput())}
                  placeholder="Ej: OptiMaxx Plus, Vida, Auto..."
                  className="flex-1 border rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                />
                <Button type="button" variant="outline" onClick={addBtFromInput}>
                  Agregar
                </Button>
              </div>

              {allBusinesses.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {allBusinesses.map(bt => (
                    <Badge
                      key={bt}
                      variant="secondary"
                      className="flex items-center gap-1 px-2 py-1 cursor-pointer hover:bg-red-100"
                      onClick={() => removeBtFromStep0(bt)}
                    >
                      {bt}
                      <span className="text-muted-foreground ml-0.5">×</span>
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Ingresa el nombre del producto o tipo de negocio y presiona Agregar.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                Cancelar
              </Button>
              <Button
                onClick={handleStep0Continue}
                disabled={allBusinesses.length === 0}
                className="bg-[#1a365d] hover:bg-[#1a365d]/90"
              >
                Siguiente <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </>
        )}

        {/* ── Step 1: Business selection ── */}
        {step === 1 && (
          <>
            <div className="space-y-3 py-2">
              <div
                onClick={toggleAll}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  allSelected
                    ? 'border-[#1a365d] bg-blue-50 dark:bg-blue-900/20'
                    : 'border-border hover:bg-muted/40'
                }`}
              >
                <Checkbox
                  id="select-all"
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  onClick={e => e.stopPropagation()}
                />
                <Label htmlFor="select-all" className="cursor-pointer font-semibold text-gray-800">
                  Todos los negocios
                </Label>
              </div>
              <div className="border-l-2 border-muted ml-3 pl-4 space-y-2">
                {allBusinesses.map(bt => (
                  <div
                    key={bt}
                    onClick={() => toggleBusiness(bt)}
                    className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-muted/40 transition-colors"
                  >
                    <Checkbox
                      id={`bt-${bt}`}
                      checked={selectedBusinesses.includes(bt)}
                      onCheckedChange={() => toggleBusiness(bt)}
                      onClick={e => e.stopPropagation()}
                    />
                    <Label htmlFor={`bt-${bt}`} className="cursor-pointer text-gray-800">{bt}</Label>
                  </div>
                ))}
              </div>
              {remaining.length > 0 && (
                <p className="text-xs text-amber-700 flex items-start gap-1.5 mt-2 p-2 bg-amber-50 rounded-lg">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>
                    El lead quedará <strong>En Negociación</strong> con los planes restantes:&nbsp;
                    {remaining.join(', ')}
                  </span>
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancelar</Button>
              <Button
                onClick={() => setStep(2)}
                disabled={selectedBusinesses.length === 0}
                className="bg-[#1a365d] hover:bg-[#1a365d]/90"
              >
                Siguiente <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </>
        )}

        {/* ── Step 2: Policy form ── */}
        {step === 2 && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-2 gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm">
              <div className="flex items-center gap-2">
                <User className="h-3.5 w-3.5 text-[#1a365d] shrink-0" />
                <span className="text-gray-600">Cliente:</span>
                <span className="font-medium text-gray-800 truncate">{lead.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-3.5 w-3.5 text-[#1a365d] shrink-0" />
                <span className="text-gray-600">Agente:</span>
                <span className="font-medium text-gray-800 truncate">{lead.assigned_agent_name || '—'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Building2 className="h-3.5 w-3.5 text-[#1a365d] shrink-0" />
                <span className="text-gray-600">Aseguradora:</span>
                <span className="font-medium text-gray-800">{formatBusinessLine(form.aseguradora) || '—'}</span>
              </div>
              <div className="flex items-center gap-2 col-span-2">
                <Package className="h-3.5 w-3.5 text-[#1a365d] shrink-0" />
                <span className="text-gray-600">Negocios:</span>
                <div className="flex flex-wrap gap-1">
                  {(selectedBusinesses.length > 0 ? selectedBusinesses : allBusinesses).map((t, i) => (
                    <Badge key={i} variant="outline" className="text-xs text-[#1a365d] border-[#1a365d]/30">{t}</Badge>
                  ))}
                </div>
              </div>
              {remaining.length > 0 && (
                <div className="col-span-2 text-xs text-amber-700 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3 shrink-0" />
                  Quedan en negociación: {remaining.join(', ')}
                </div>
              )}
            </div>

            {/* ── Pending payment notice ── */}
            {existingPolicy && (
              <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>
                  Este lead ya tiene póliza <strong>{existingPolicy.policy_number}</strong> pendiente de pago.
                  Al confirmar, solo se activarán el cliente y la póliza existente — no se creará ningún registro nuevo.
                </span>
              </div>
            )}

            {/* ── Client data ── */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-[#1a365d] uppercase tracking-wide">
                Datos del cliente
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="client_email" className="text-gray-700">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="client_email"
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={form.email}
                    onChange={e => handleChange('email', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="client_phone" className="text-gray-700">
                    Teléfono <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="client_phone"
                    placeholder="55 1234 5678"
                    value={form.phone}
                    onChange={e => handleChange('phone', e.target.value)}
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label htmlFor="client_birth" className="text-gray-700">
                    Fecha de nacimiento <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="client_birth"
                    type="date"
                    value={form.birth_date}
                    onChange={e => handleChange('birth_date', e.target.value)}
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label htmlFor="client_aseguradora" className="text-gray-700">
                    Aseguradora <span className="text-red-500">*</span>
                  </Label>
                  <Select value={form.aseguradora} onValueChange={handleAseguradoraChange}>
                    <SelectTrigger id="client_aseguradora">
                      <SelectValue placeholder="Selecciona una aseguradora" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableLines.filter(l => l.id !== 'all').map(l => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* ── Policy data ── */}
            <div className="space-y-3">
              <p className="text-xs font-semibold text-[#1a365d] uppercase tracking-wide">
                Datos de la póliza
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1">
                  <Label htmlFor="policy_number" className="text-gray-700">
                    Número de póliza <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="policy_number"
                    placeholder="Ej. ALZ-2026-001234"
                    value={form.policy_number}
                    onChange={e => handleChange('policy_number', e.target.value)}
                  />
                </div>

                {/* Product type selector */}
                <div className="col-span-2 space-y-1">
                  <Label htmlFor="product_type" className="text-gray-700">Tipo de producto</Label>
                  <Select value={form.product_type} onValueChange={handleProductChange}>
                    <SelectTrigger id="product_type">
                      <SelectValue placeholder="Seleccionar producto (opcional)..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(productsByGroup).length > 0
                        ? Object.entries(productsByGroup).map(([group, names]) => (
                            <SelectGroup key={group}>
                              <SelectLabel>{group}</SelectLabel>
                              {names.map(name => (
                                <SelectItem key={name} value={name}>{name}</SelectItem>
                              ))}
                            </SelectGroup>
                          ))
                        : <SelectItem value="__none__" disabled>Selecciona una aseguradora primero</SelectItem>
                      }
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="start_date" className="text-gray-700">Vigencia desde <span className="text-red-500">*</span></Label>
                  <Input id="start_date" type="date" value={form.start_date} onChange={e => handleChange('start_date', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="end_date" className="text-gray-700">Vigencia hasta <span className="text-red-500">*</span></Label>
                  <Input id="end_date" type="date" value={form.end_date} onChange={e => handleChange('end_date', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="premium" className="text-gray-700">Prima (MXN)</Label>
                  <Input id="premium" type="number" min="0" step="0.01" placeholder="0.00" value={form.premium} onChange={e => handleChange('premium', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="contributions" className="text-gray-700">Aportaciones (MXN)</Label>
                  <Input id="contributions" type="number" min="0" step="0.01" placeholder="0.00" value={form.contributions} onChange={e => handleChange('contributions', e.target.value)} />
                </div>
              </div>

              {/* Dynamic product fields */}
              {productFields.length > 0 && (
                <>
                  <Separator />
                  <p className="text-xs font-semibold text-[#1a365d] uppercase tracking-wide">
                    Detalles — {form.product_type}
                  </p>
                  {regularFields.length > 0 && (
                    <div className="grid grid-cols-2 gap-3">
                      {regularFields.map(field => (
                        <div key={field.key} className={field.type === 'textarea' ? 'col-span-2' : ''}>
                          <DynamicField
                            field={field}
                            value={detalles[field.key]}
                            onChange={val => setDetallesField(field.key, val)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {checkboxFields.length > 0 && (
                    <div className="space-y-1 pt-1">
                      {checkboxFields.map(field => (
                        <DynamicField
                          key={field.key}
                          field={field}
                          value={detalles[field.key]}
                          onChange={val => setDetallesField(field.key, val)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Payment question */}
              <div className="space-y-2 pt-1">
                <Label className="text-sm font-semibold text-gray-800">¿El cliente ya realizó el pago?</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleChange('payment_done', true)}
                    className={`flex-1 flex items-center justify-center gap-2 p-2.5 rounded-lg border text-sm font-medium transition-colors ${
                      form.payment_done
                        ? 'bg-[#1a365d] border-[#1a365d] text-white'
                        : 'border-border text-gray-700 hover:bg-muted'
                    }`}
                  >
                    <CheckCircle className="h-4 w-4" />
                    Sí, ya pagó
                  </button>
                  <button
                    type="button"
                    onClick={() => handleChange('payment_done', false)}
                    className={`flex-1 flex items-center justify-center gap-2 p-2.5 rounded-lg border text-sm font-medium transition-colors ${
                      !form.payment_done
                        ? 'bg-[#1a365d] border-[#1a365d] text-white'
                        : 'border-border text-gray-700 hover:bg-muted'
                    }`}
                  >
                    <AlertCircle className="h-4 w-4" />
                    No, pendiente de pago
                  </button>
                </div>
                {!form.payment_done && (
                  <p className="text-xs text-gray-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3 shrink-0" />
                    El cliente aparecerá en Clientes con estado "Pendiente de pago". Se enviará notificación diaria al agente.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-between gap-2 pt-2 border-t">
              {/* Back button: to step 0 if lead had no businesses, to step 1 if multiple */}
              {(!lead.business_types?.length && !lead.business_plan) ? (
                <Button variant="outline" onClick={() => setStep(0)} disabled={loading}>
                  <ChevronLeft className="h-4 w-4 mr-1" /> Volver
                </Button>
              ) : allBusinesses.length > 1 ? (
                <Button variant="outline" onClick={() => setStep(1)} disabled={loading}>
                  <ChevronLeft className="h-4 w-4 mr-1" /> Volver
                </Button>
              ) : (
                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancelar</Button>
              )}
              <div className="flex gap-2">
                {((!lead.business_types?.length && !lead.business_plan) || allBusinesses.length > 1) && (
                  <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancelar</Button>
                )}
                <Button onClick={handleSubmit} disabled={loading} className="bg-green-600 hover:bg-green-700">
                  <FileText className="h-4 w-4 mr-2" />
                  {loading ? 'Procesando…' : allClosed ? 'Convertir y crear póliza' : 'Conversión parcial'}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PolicyConversionModal;
